import { useMemo, useCallback } from "react";

/**
 * Shared hook for Notification action component logic across all contexts
 * @param {string} notificationType - 'email' | 'slack' | 'sms' | 'front' (not used currently but kept for consistency)
 * @param {string} context - 'root' | 'offHours' | 'ivrMenu' | 'offHoursIvrMenu' | 'ivrMenuOffHours'
 * @param {object} route - Route state object
 * @param {function} setRoute - Route state setter
 * @param {string|null} ivrOptionId - IVR menu option ID (required for 'ivrMenu' contexts)
 * @param {boolean} isOffHoursInIvr - Whether this is the off-hours section within an IVR menu option
 * @returns {object} - Notification action state and handlers
 */
export const useNotificationAction = (notificationType, context, route, setRoute, ivrOptionId = null, isOffHoursInIvr = false, optionKey = null) => {
  // Options are object { "0": {...}, "1": {...} }. Find option by id.
  const getOptionFromOptions = (options, id) => {
    if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
    return Object.values(options).find((opt) => opt?.id === id) ?? null;
  };
  const getOptionKeyById = (options, id) => {
    if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
    const entry = Object.entries(options).find(([, opt]) => opt?.id === id);
    return entry ? entry[0] : null;
  };

  // Get channel value based on context
  const channelValue = useMemo(() => {
    if (context === 'ivrMenu') {
      const option = optionKey != null
        ? route?.ivrMenu?.options?.[optionKey]
        : getOptionFromOptions(route?.ivrMenu?.options, ivrOptionId);
      if (isOffHoursInIvr) {
        return option?.offHoursChannel || "";
      }
      return option?.primaryChannel || "";
    } else if (context === 'offHoursIvrMenu') {
      const option = optionKey != null
        ? route?.offHoursIvrMenu?.options?.[optionKey]
        : getOptionFromOptions(route?.offHoursIvrMenu?.options, ivrOptionId);
      return option?.primaryChannel || "";
    } else if (context === 'offHours') {
      return route?.offHoursChannel || "";
    } else {
      return route?.primaryChannel || "";
    }
  }, [context, route, ivrOptionId, isOffHoursInIvr, optionKey]);

  // Handle channel change (options are object)
  const handleChannelChange = useCallback((value) => {
    if (context === 'ivrMenu') {
      setRoute((prev) => {
        const key = optionKey != null ? optionKey : getOptionKeyById(prev.ivrMenu?.options, ivrOptionId);
        if (key == null) return prev;
        const options = prev.ivrMenu?.options ?? {};
        return {
          ...prev,
          ivrMenu: {
            ...prev.ivrMenu,
            options: {
              ...options,
              [key]: {
                ...options[key],
                ...(isOffHoursInIvr ? { offHoursChannel: value } : { primaryChannel: value }),
              },
            },
          },
        };
      });
    } else if (context === 'offHoursIvrMenu') {
      setRoute((prev) => {
        const key = optionKey != null ? optionKey : getOptionKeyById(prev.offHoursIvrMenu?.options, ivrOptionId);
        if (key == null) return prev;
        const options = prev.offHoursIvrMenu?.options ?? {};
        return {
          ...prev,
          offHoursIvrMenu: {
            ...prev.offHoursIvrMenu,
            options: {
              ...options,
              [key]: { ...options[key], primaryChannel: value },
            },
          },
        };
      });
    } else if (context === 'offHours') {
      setRoute((prev) => ({
        ...prev,
        offHoursChannel: value,
      }));
    } else {
      setRoute((prev) => ({
        ...prev,
        primaryChannel: value,
      }));
    }
  }, [context, setRoute, ivrOptionId, isOffHoursInIvr, optionKey]);

  // Handle remove notification (options are object)
  const handleRemove = useCallback(() => {
    if (context === 'ivrMenu') {
      setRoute((prev) => {
        const key = optionKey != null ? optionKey : getOptionKeyById(prev.ivrMenu?.options, ivrOptionId);
        if (key == null) return prev;
        const options = prev.ivrMenu?.options ?? {};
        return {
          ...prev,
          ivrMenu: {
            ...prev.ivrMenu,
            options: {
              ...options,
              [key]: {
                ...options[key],
                ...(isOffHoursInIvr 
                  ? { offHoursChannel: null, offHoursNotification: null }
                  : { primaryChannel: null, primaryNotification: null }
                ),
              },
            },
          },
        };
      });
    } else if (context === 'offHoursIvrMenu') {
      setRoute((prev) => {
        const key = optionKey != null ? optionKey : getOptionKeyById(prev.offHoursIvrMenu?.options, ivrOptionId);
        if (key == null) return prev;
        const options = prev.offHoursIvrMenu?.options ?? {};
        return {
          ...prev,
          offHoursIvrMenu: {
            ...prev.offHoursIvrMenu,
            options: {
              ...options,
              [key]: {
                ...options[key],
                primaryChannel: null,
                primaryNotification: null,
              },
            },
          },
        };
      });
    } else if (context === 'offHours') {
      setRoute((prev) => ({
        ...prev,
        offHoursChannel: null,
        offHoursNotification: null,
      }));
    } else {
      setRoute((prev) => ({
        ...prev,
        primaryChannel: null,
        primaryNotification: null,
      }));
    }
  }, [context, setRoute, ivrOptionId, isOffHoursInIvr, optionKey]);

  // Determine styling based on context
  const containerClass = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return "bg-gray-50 border border-gray-100 rounded-lg p-3 mb-2";
    } else {
      return "bg-white border-2 border-gray-200 rounded-xl p-6 space-y-4 shadow-sm";
    }
  }, [context]);

  const inputClass = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return "w-full bg-transparent border-b-[1px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
    } else {
      return "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
    }
  }, [context]);

  const headerSize = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return "font-medium text-[13px]";
    } else {
      return "font-bold text-[18px]";
    }
  }, [context]);

  const subtitleSize = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return "text-[12px]";
    } else {
      return "text-[13px]";
    }
  }, [context]);

  const useIVRStyle = context === 'ivrMenu' || context === 'offHoursIvrMenu';

  return {
    channelValue,
    handleChannelChange,
    handleRemove,
    containerClass,
    inputClass,
    headerSize,
    subtitleSize,
    useIVRStyle,
  };
};
